// API Response Types

export interface LoginRequest {
  username: string;
  password: string;
}

export interface User {
  id: number;
  fullName: string;
  roleId: number;
  mcr: string;
  hasSignature: boolean;
  // Fields for FormattedSignatureLine (matching CheckLine's Employee)
  displayName: string;
  customText1: string;
  customText2: string;
  maskOn: boolean;
  /** hr.Employee.Code — Management Panel allowlist (LKH, LSC, DMI, TWH, Admin, OWH) or SysAdmin role. */
  code?: string;
  /** CheckLine login username — Management also matches the allowlist here. */
  username?: string;
}

export interface LoginResponse {
  accessToken: string;
  tokenType: string;
  expiresIn: number;
  user: User;
}

export interface ErrorResponse {
  message: string;
  errorCode: string;
}

export interface StoreListItem {
  id: number;
  code: string;
  storeName: string;
}

export interface TeleVisitQueueItem {
  visitULID: string;
  queueNo: string;
  patientName: string;
  nric: string;
  age: number;
  gender: string;
  storeID: number;
  storeName: string;
  storeCode?: string;
  visitDate: string;
  visitTime: string;
  temperature?: number;
  sbp?: number;
  dbp?: number;
  pulse?: number;
  spO2?: number;
  remarks?: string;
  allergies: string[];
  chronicConditions: string[];
  // Chronic condition diagnosis codes - matching CheckLine's PrevCHRDiagnosisCodeList for exact ColorPreviousCHR matching
  chronicConditionCodes?: string[];
  /** Latest `med.TeleAutoTriageConsult.Summary` for queue triage bullets. */
  triageSummary?: string | null;
  // Visit stage (CheckLine): Reg=0, CheckIn=10, Triage=20, SeenDr=30, GivenMed=40, Payment=50, CheckOut=60, Cancel=99.
  // Staff tele colours: blue <30, red 30–59, green 60+, gray 99.
  visitStage: number;
  /** med.Visit.VisitType — 999 tele consult, 991 med fulfillment, etc. */
  visitType?: number;
  // Visit type flags for Onsite Tele / Offsite Tele tags
  walkIn: boolean;
  teleMed: boolean;
  /** First-time patient for this visit (med.Visit.NewPt). */
  newPt?: boolean;
  // Corporate Company name (from Customer_CorporateCompany, matching CheckLine's CorporateCompanyInfoActive)
  corporateCompanyName?: string;
  /** Visit.EmpId — null/0 means unpicked */
  empId?: number | null;
  /** Employee.Code for the doctor who picked this visit (when empId is set). */
  empCode?: string | null;
  /** med.TeleChatConversation.InchargeEmpID — staff who claimed chat for this visit */
  chatInchargeEmpId?: number | null;
  /** hr.Employee.Code for chat incharge */
  chatInchargeEmpCode?: string | null;
  /** Patient messages after last staff reply (queue API) — badge on queue tile */
  unreadPatientChatCount?: number;
  /** New patient: latest portal Patient ID not yet vetted by staff. */
  patientIdVettingPending?: boolean;
  /** Patient-portal visit: ID verified but Stripe payment not completed. */
  patientPaymentPending?: boolean;
  /** Google Meet URL when already generated (med.VisitTeleConsult). */
  googleMeetUrl?: string | null;
  /** Corporate visit: shared Zoom room in use by another patient until doctor finishes consult. */
  corporateZoomSessionBlocked?: boolean;
  /** Partner / promo code from Stripe checkout (e.g. SGAIR, 57MGE). */
  consultPartnerCode?: string | null;
  /** Latest med.CHASBalance (CHAS badge). */
  chas?: ChasStatus | null;
  /** Active HSS (Haze Subsidy Scheme) claim on this visit. */
  hssApplied?: boolean;
  /** HSS flags (doctor screen). */
  hss?: HssSummary | null;
  /** Doctor clicked Finish consult (VisitTimeStamp step 36). */
  consultFinalized?: boolean;
  /** Patient-portal auto checkout is on for this environment. */
  autoCheckoutEnabled?: boolean;
  /** Patient still needs to choose pickup or delivery on the portal. */
  needsFulfillmentChoice?: boolean;
  /** Patient still needs to pay remaining balance on the portal. */
  needsExtraPayment?: boolean;
  /**
   * Aspen Meet only: patient is currently in the room.
   * Null when the visit has no Aspen Meet share URL.
   */
  patientInRoom?: boolean | null;
}

export interface PatientDetails {
  customerULID: string;
  fullName: string;
  nric: string;
  birthDate?: string;
  age: number;
  gender: string;
  storeID: number;
  storeName: string;
  temperature?: number;
  sbp?: number;
  dbp?: number;
  pulse?: number;
  spo2?: number;
  weight?: number;
  height?: number;
  waist?: number;
  remarks?: string;
  allergies: string[];
  chronicConditions: string[];
  // Chronic condition diagnosis codes - matching CheckLine's PrevCHRDiagnosisCodeList for exact ColorPreviousCHR matching
  chronicConditionCodes?: string[];
  // Current visit's existing diagnosis codes (matching CheckLine's PopulateACTDiagnoses/PopulateCHRDiagnoses)
  currentVisitAcuteDiagnosisCodes?: string[];
  currentVisitChronicDiagnosisCodes?: string[];
  empId?: number; // Doctor/Employee ID who picked the patient (matching CheckLine's Visit.EmpID)
  /** Employee.Code for the doctor who picked this visit */
  empCode?: string | null;
  // Visit type flags for Onsite Tele / Offsite Tele tags
  walkIn?: boolean;
  teleMed?: boolean;
  /** First-time patient for this visit (med.Visit.NewPt). */
  newPt?: boolean;
  // Corporate Company name (from Customer_CorporateCompany, matching CheckLine's CorporateCompanyInfoActive)
  corporateCompanyName?: string;
  /** Latest `med.TeleAutoTriageConsult.Summary` for this visit (AI triage bullets). */
  triageSummary?: string | null;
  /** med.Visit.Stage — SeenDr (30+) means consult finished. */
  visitStage?: number;
  /** Doctor clicked Finish consult (VisitTimeStamp step 36). */
  consultFinalized?: boolean;
  /** True when this visit runs portal auto-checkout (offsite only). */
  autoCheckoutEnabled?: boolean;
  /** Latest med.CHASBalance (CHAS badge). */
  chas?: ChasStatus | null;
  /** HSS flags; full panel via GET /api/TeleVisit/{visitULID}/hss. */
  hss?: HssSummary | null;
}

/** Portal-uploaded NRIC/passport (med.CustomerAttachment, document type Patient ID). */
export interface PatientIdDocument {
  attachmentUlid: string;
  fileName: string;
  extension: string;
  dateCreated: string;
  dateDocument?: string | null;
  documentType: string;
  dateVetted?: string | null;
  vettedBy?: number | null;
  vettedByName?: string | null;
  vettingRemark?: string | null;
  isVettingPending?: boolean;
  vettingApproved?: boolean | null;
  /** Front / Back / Passport */
  sideLabel?: string | null;
}

export interface PatientIdAiFieldMatch {
  registered?: string | null;
  document?: string | null;
  match: boolean;
  confidence: number;
}

export interface PatientIdAiVerification {
  verificationId: string;
  status: string;
  score: number;
  detectedDocumentType?: string;
  submittedDocumentType?: string;
  identityMatch?: boolean;
  documentValid?: boolean;
  documentTypeMatch?: boolean;
  fraudRisk?: string;
  manualReviewRequired?: boolean;
  reasons?: string[];
  criticalFailures?: string[];
  fieldMatches?: Record<string, PatientIdAiFieldMatch>;
  aiProvider?: string | null;
  aiModel?: string | null;
  createdAt?: string;
}

export interface PatientIdDocumentReview {
  documents: PatientIdDocument[];
  patientFullName?: string | null;
  patientNric?: string | null;
  patientDateOfBirth?: string | null;
  aiVerification?: PatientIdAiVerification | null;
}

export interface PatientIdDocumentVetRequest {
  attachmentUlid?: string;
  approved: boolean;
  vettingRemark?: string;
}

export interface CaseNoteHistory {
  ulid: string;
  dateNote: string;
  storeName: string;
  doctorName: string;
  diagnosis: string;
  entry: string;
}

export interface DispenseHistoryItem {
  itemName: string;
  quantity: number;
  unitPrice: number;
  itemTotal: number;
  dose?: string;
  frequency?: string;
}

export interface DispenseHistory {
  visitULID: string;
  visitDate: string;
  storeName: string;
  items: DispenseHistoryItem[];
  totalAmount: number;
}

export type ItemType = 'MED' | 'PRC' | 'LAB' | 'RAD';

export interface Item {
  id: number;
  itemCode: string;
  itemName: string;
  publishName: string;
  price: number;
  itemType: ItemType;
  uom_Dose?: string;
  uOM_Disp?: string;
  isCDMPClaimable: boolean;
  isCHASClaimable: boolean;
  isMedisaveClaimable: boolean;
  isManagedCare: boolean;
  isVaccine: boolean;
  defaultDose?: string;
  defaultFrequency?: string;
  defaultDispenseQty?: number;
  defaultInstruction?: string;
  defaultPurpose?: string;
  defaultPrecaution?: string;
}

export interface Diagnosis {
  id: number;
  code: string;
  term: string;
  type: 'ACT' | 'CHR';
  chasDiagCode?: string;
}

export interface VisitOrderItem {
  ulid: string;
  itemID: number;
  itemCode: string;
  itemName: string;
  publishName: string;
  quantity: number;
  unitPrice: number;
  dosage?: number;
  doseUOM?: string;
  dispenseUOM?: string;
  frequency?: string;
  instruction?: string;
  purpose?: string;
  precaution?: string;
  itemType: number; // 1=Drug, 2=Lab, 3=Procedure
  isCHASClaimable: boolean;
  isMedisaveClaimable: boolean;
  isCDMPClaimable: boolean;
  isManagedCare: boolean;
}

export interface SaveDispenseItem {
  itemID: number;
  itemCode: string;
  itemName: string;
  publishName: string;
  quantity: number;
  unitPrice: number;
  dosage?: number;
  doseUOM?: string;
  dispenseUOM?: string;
  frequency?: string;
  instruction?: string;
  purpose?: string;
  precaution?: string;
  isCHASClaimable: boolean;
  isMedisaveClaimable: boolean;
  isCDMPClaimable: boolean;
  isManagedCare: boolean;
  itemType: number; // 1=Drug, 2=Lab, 3=Procedure
}

export interface SaveDispenseRequest {
  visitULID: string;
  caseNote?: string;
  items: SaveDispenseItem[];
  acuteDiagnosisCodes: string[];
  chronicDiagnosisCodes: string[];
  /** Doctor's Haze (HSS) choice; used when Save auto-finishes the consult. Omit = server default. */
  hss?: HssSelection | null;
}

export interface SaveDispenseResponse {
  success: boolean;
  message: string;
  visitULID?: string;
}

export interface SaveSignatureRequest {
  empID: number;
  multiUse: boolean;
  signatureData: string; // base64
}

export interface SignatureResponse {
  signatureULID: string;
  hasSignature: boolean;
  signatureDataBase64?: string;
}

export interface CreateMCRequest {
  visitULID: string;
  dateStart: string; // ISO date string (YYYY-MM-DD)
  days: number;
  type?: number; // MedCertType: 0=Normal, 1=OutpatientSickLeave, 2=Hospitalization, etc.
  excuseDetail?: string;
  remarks?: string;
  signatureULID?: string;
  isCourt?: boolean;
}

export interface CreateMCResponse {
  success: boolean;
  message: string;
  mcULID?: string;
}

export interface UpdateMCRequest {
  mcULID: string;
  dateStart: string; // ISO date string (YYYY-MM-DD)
  days: number;
  excuseDetail?: string;
  remarks?: string;
}

export interface UpdateMCResponse {
  success: boolean;
  message: string;
}

export interface DeleteMCResponse {
  success: boolean;
  message: string;
}

// Memo Types (matching CheckLine's MemoType enum from EnumGenerator.generated.vb)
// CheckLine values: Memo=1, Referral=2, Prescription=3, Vaccination=4, Chickenpox=5, HFMD=6, POCT=7, HAJ=8
export enum MemoType {
  Memo = 1,
  Referral = 2,
  Prescription = 3,
  Vaccination = 4,
  Chickenpox = 5,
  HFMD = 6,
  POCT = 7,
  HAJ = 8
}

export interface VisitMemo {
  ulid: string;
  visitULID: string;
  customerULID: string;
  storeID?: number;
  memoType: number; // MemoType enum value
  recipient: string;
  subHeader: string;
  body: string;
  signOff: string;
  signatureID?: number;
  activeFrom?: string;
  activeTill?: string;
  memoDate?: string;
  signatureULID?: string;
  memoTypeName?: string; // Display name
}

export interface SaveMemoRequest {
  visitULID: string;
  memoULID?: string; // For updates - undefined for new memos
  customerULID: string;
  memoType: number; // MemoType enum
  recipient: string;
  subHeader: string;
  body: string;
  signOff: string;
  memoDate?: string; // ISO date string
  signatureULID?: string;
}

export interface SaveMemoResponse {
  success: boolean;
  message: string;
  memoULID?: string;
}

export interface DeleteMemoResponse {
  success: boolean;
  message: string;
}

// Component prop types
export interface Doctor {
  id: number;
  name: string;
  mcr: string;
  // Fields for FormattedSignatureLine (matching CheckLine's Employee)
  displayName: string;
  customText1: string;
  customText2: string;
  maskOn: boolean;
  /** hr.Employee.Code — Management Panel allowlist or SysAdmin role. */
  code?: string;
  username?: string;
  roleId?: number;
}

export interface Patient {
  id: number;
  visitULID: string;
  queueNo: string;
  name: string;
  nric: string;
  age: number;
  gender: 'M' | 'F';
  store: string;
  storeId: number;
  storeCode?: string;
  visitTime: string;
  temp: number;
  sbp: number;
  dbp: number;
  pulse: number;
  spo2: number;
  weight?: number;
  height?: number;
  waist?: number;
  remarks: string;
  allergies: string[];
  chronicConditions: string[];
  // Chronic condition diagnosis codes - matching CheckLine's PrevCHRDiagnosisCodeList for exact ColorPreviousCHR matching
  chronicConditionCodes?: string[];
  /** AI tele triage summary (queue API); shown as bullets on queue cards. */
  triageSummary?: string | null;
  // Current visit's existing diagnosis codes (matching CheckLine's PopulateACTDiagnoses/PopulateCHRDiagnoses)
  currentVisitAcuteDiagnosisCodes?: string[];
  currentVisitChronicDiagnosisCodes?: string[];
  empId?: number; // Doctor/Employee ID who picked the patient (matching CheckLine's Visit.EmpID)
  /** Employee.Code for the doctor who picked this visit */
  empCode?: string;
  // Visit stage (med.Visit.Stage, CheckLine): Reg=0, CheckIn=10, Triage=20, SeenDr=30, GivenMed=40, Payment=50, CheckOut=60, Cancel=99.
  // Staff teleconsult UI: blue when stage < 30 (waiting), red 30–59 (consulting), green 60+ (checked out), gray 99 (cancelled).
  visitStage?: number;
  /** med.Visit.VisitType — 999 tele consult, 991 med fulfillment, etc. */
  visitType?: number;
  // Visit type flags for Onsite Tele / Offsite Tele tags
  walkIn?: boolean;
  teleMed?: boolean;
  /** First-time patient for this visit (med.Visit.NewPt). */
  newPt?: boolean;
  // Corporate Company name (from Customer_CorporateCompany, matching CheckLine's CorporateCompanyInfoActive)
  corporateCompanyName?: string;
  /** Staff who claimed patient ↔ staff chat (med.TeleChatConversation.InchargeEmpID) */
  chatInchargeEmpId?: number | null;
  /** hr.Employee.Code for chat incharge */
  chatInchargeEmpCode?: string | null;
  /** From queue API; staff tele UI may override when chat dock is open */
  unreadPatientChatCount?: number;
  /** New patient: portal Patient ID awaiting staff vetting. */
  patientIdVettingPending?: boolean;
  /** Patient-portal visit: ID verified but payment not completed. */
  patientPaymentPending?: boolean;
  /** Google Meet URL when already generated (med.VisitTeleConsult). */
  googleMeetUrl?: string | null;
  /** Corporate visit: shared Zoom room in use by another patient until doctor finishes consult. */
  corporateZoomSessionBlocked?: boolean;
  /** Partner / promo code from Stripe checkout (e.g. SGAIR, 57MGE). */
  consultPartnerCode?: string | null;
  /** Latest med.CHASBalance (CHAS badge). */
  chas?: ChasStatus | null;
  /** Active HSS (Haze Subsidy Scheme) claim on this visit. */
  hssApplied?: boolean;
  /** HSS flags (doctor screen). */
  hss?: HssSummary | null;
  /** Doctor clicked Finish consult (VisitTimeStamp step 36). */
  consultFinalized?: boolean;
  /** Patient-portal auto checkout is on for this environment. */
  autoCheckoutEnabled?: boolean;
  /** Patient still needs to choose pickup or delivery on the portal. */
  needsFulfillmentChoice?: boolean;
  /** Patient still needs to pay remaining balance on the portal. */
  needsExtraPayment?: boolean;
  /**
   * Aspen Meet only: patient is currently in the room.
   * Null when the visit has no Aspen Meet share URL.
   */
  patientInRoom?: boolean | null;
}
export interface DrugAllergyItem {
  code: string;
  description: string;
}

export interface DrugAllergyDetail {
  ulid: string;
  drugCode: string;
  drugName: string;
  drugProbabilityCode?: string;
  reactionCode?: string;
  outcomeCode?: string;
  informationSourceCode?: string;
  drugAllergyIndicatorCode?: string;
  alertIndicatorCode?: string;
  dateCreated?: string;
}

export interface SaveDrugAllergyRequest {
  customerULID: string;
  drugCode: string;
  drugProbabilityCode?: string;
  reactionCode?: string;
  outcomeCode?: string;
  informationSourceCode?: string;
  drugAllergyIndicatorCode?: string;
  alertIndicatorCode?: string;
  activeTill?: string;
}

export interface DrugAllergyValidation {
  hasDrugAllergyRecord: boolean; // True if customer has any drug allergy record (including NIL)
}

export interface CodeSetItem {
  code: string;
  description: string;
}

/** GET /api/TeleVisit/{id}/meet-status — Meet API v2 live status */
export interface TeleVisitMeetStatus {
  source: string;
  hasActiveConference: boolean;
  activeParticipantCount: number | null;
  patientInRoom?: boolean;
  doctorInRoom?: boolean;
  staffInRoom?: boolean;
  patientBrowser?: string | null;
  patientAudioMuted?: boolean | null;
  message?: string | null;
}

// External Consult Link types
export type PortalMode = 'doctor' | 'staff';

export interface ValidateTokenResponse {
  valid: boolean;
  visitULID?: string;
  expiresAt?: string;
  pickedByEmpId?: number; // EmpId of doctor who already picked the visit (null/undefined if not picked)
  pickedByDoctorName?: string; // Display name of doctor who picked the visit
  message: string;
}

/** POST /api/TeleVisit/teleconsult-staff-chat — same JSON shape as patient portal staff chat */
export type TeleStaffChatSender = 'patient' | 'staff' | 'system' | 'partner';

export interface TeleStaffChatMessage {
  sender: TeleStaffChatSender;
  text: string;
  ts: number;
  imageUlid?: string;
  imageFileName?: string;
  imageMimeType?: string;
  /**
   * Required on write for staff lines (use staffChatSenderFields).
   * Older stored JSON may omit these; GET may enrich names for display only.
   */
  staffName?: string;
  /** Required on write for staff lines — hr.Employee.ID of the poster. */
  staffEmpId?: number;
}

export interface TeleStaffChatGetResponse {
  chatConversation: string;
  inchargeEmpId?: number | null;
  inchargeEmpCode?: string | null;
  /** Incharge display name. Historical messages have no per-line author, so the transcript falls back to this. */
  inchargeDisplayName?: string | null;
  isClosed?: boolean;
  dateClosed?: string | null;
}

/** GET /api/TeleVisit/teleconsult-normal-support-queue */
export interface TeleNormalSupportQueueItem {
  chatUlid: string;
  visitorName: string;
  visitorPhone: string;
  dateCreated: string;
  inchargeEmpId?: number | null;
  inchargeEmpCode?: string | null;
  awaitingStaffReply: boolean;
  patientMessageCount: number;
  lastMessagePreview?: string | null;
  lastMessageAt?: string | null;
  isClosed?: boolean;
  isClosingPending?: boolean;
  dateClosed?: string | null;
  isCorporatePartner?: boolean;
  corporateCompanyCode?: string | null;
  displaySubtitle?: string | null;
  visitorEmail?: string | null;
}

/** GET /api/TeleVisit/teleconsult-payment-follow-up */
export type TelePaymentFollowUpStatus = 'awaitingPayment' | 'paymentTimedOut';

export interface TelePaymentFollowUpItem {
  visitUlid: string;
  customerUlid: string;
  patientName: string;
  phone: string;
  email?: string | null;
  status: TelePaymentFollowUpStatus;
  registeredAt?: string | null;
  cancelledAt?: string | null;
  paymentReceivedAfterCancel?: boolean;
  existingChatUlid?: string | null;
  chatInchargeEmpId?: number | null;
  chatInchargeEmpCode?: string | null;
}

/** POST /api/TeleVisit/teleconsult-stripe-checkout-session */
export interface TeleconsultStripeCheckoutSessionResponse {
  checkoutUrl: string;
  sessionId?: string | null;
}

/** GET /api/TeleVisit/teleconsult-portal-config */
export interface TeleconsultPortalConfig {
  patientPortalOrigin: string;
}

/** POST /api/ExternalConsult/generate-consult-link */
export interface GenerateConsultLinkResponse {
  consultUrl: string;
  expiresAt: string;
  googleMeetUrl?: string | null;
  calendarEventId?: string | null;
  calendarHtmlLink?: string | null;
  googleMeetSkipped: boolean;
  videoProvider?: string;
}

/** GET /api/TeleVisit/management/dashboard */
export interface TeleconsultManagementTypeCount {
  type: string;
  count: number;
}

export interface TeleconsultManagementDashboard {
  from: string;
  to: string;
  total: number;
  completed: number;
  cancelled: number;
  refunded: number;
  open: number;
  uniquePatients: number;
  newPatients: number;
  openWaitingId: number;
  openWaitingDoctor: number;
  pendingJuliusCheckout: number;
  stripeCollectedSgd: number;
  stripeRefundedSgd: number;
  stripeNetSgd: number;
  avgTriageSeconds?: number | null;
  avgTriageSampleCount: number;
  avgRegisterToPickSeconds?: number | null;
  avgRegisterToPickSampleCount: number;
  avgPickToConsultEndSeconds?: number | null;
  avgPickToConsultEndSampleCount: number;
  avgConsultEndToCheckoutSeconds?: number | null;
  avgConsultEndToCheckoutSampleCount: number;
  byType: TeleconsultManagementTypeCount[];
  byDoctor: TeleconsultManagementTypeCount[];
  cancelReasons: TeleconsultManagementTypeCount[];
  refundReasons: TeleconsultManagementTypeCount[];
}

export type TeleconsultManagementStatus = 'completed' | 'cancelled' | 'refunded' | 'open';

/** GET /api/TeleVisit/management/visits */
export interface TeleconsultManagementVisit {
  visitUlid: string;
  visitDate: string;
  patientName: string;
  maskedNric: string;
  type: string;
  status: TeleconsultManagementStatus | string;
  registeredAt?: string | null;
  idVerifiedAt?: string | null;
  /** Patient ID attachment upload time — start of pending→verified KPI. */
  idPendingAt?: string | null;
  patientReadyAt?: string | null;
  doctorPickedAt?: string | null;
  consultCompletedAt?: string | null;
  juliusCheckoutAt?: string | null;
  cancellationReason?: string | null;
  cancellationReasonCode?: string | null;
  cancellationReasonLabel?: string | null;
  cancellationReasonOther?: string | null;
  refunded: boolean;
  refundReason?: string | null;
  stripeAmountSgd?: number | null;
  stripeRefundedSgd?: number | null;
  stripePaidAt?: string | null;
  doctorName?: string | null;
  newPt?: boolean;
  registrationStaffName?: string | null;
  handlingStaffStep?: string | null;
  canCancel?: boolean;
  canRefund?: boolean;
  /** MC, MEDS, then memo type names (Memo, Referral, …). */
  documentLabels?: string[];
  /** TeleVisitSupport chat has ≥1 message. */
  hasChat?: boolean;
  chatMessageCount?: number | null;
}

/** GET /api/TeleVisit/management/calendar */
export interface TeleconsultManagementCalendarDay {
  date: string;
  visits: number;
  stripeSgd: number;
}

// ---------------------------------------------------------------------------
// HSS (Haze Subsidy Scheme) teleconsult + CHAS badge
// ---------------------------------------------------------------------------

/** Latest med.CHASBalance (CheckLine CHAS badge). */
export interface ChasStatus {
  known: boolean;
  cardType?: string | null;
  displayName: string;
  abbrev: string;
  color: string;
  text: string;
  expiryDate?: string | null;
  isExpired: boolean;
  statusCode?: string | null;
  statusDesc?: string | null;
  dateRequest?: string | null;
  isToday: boolean;
  tier?: number | null;
  balanceAmount?: number | null;
  patientVisitBalance?: number | null;
  isSingaporean: boolean;
  refreshQueued: boolean;
  refreshAvailable: boolean;
}

/** Doctor's Haze choice sent with Save Dispense / Finish consult. */
export interface HssSelection {
  apply: boolean;
  /** Haze condition codes (J069, J304, ...). Null = every haze condition on the visit. */
  conditions?: string[] | null;
}

export interface HssSummary {
  featureEnabled: boolean;
  applied: boolean;
  offerable: boolean;
  eligible: boolean;
  reason?: string | null;
}

export interface HssCondition {
  code: string;
  name: string;
  isChronic: boolean;
  selected: boolean;
  limitReached: boolean;
  claimable: boolean;
  usedInWindow: number;
  maxInWindow: number;
  windowStart?: string | null;
  windowEnd?: string | null;
  priorDates: string[];
  message: string;
}

export interface HssBillLine {
  name: string;
  itemCode?: string | null;
  amount: number;
}

export interface HssBillPreview {
  thisBill: number;
  eligibleBill: number;
  eligibleLines: HssBillLine[];
  nonClaimableLines: HssBillLine[];
  prepaidLines: HssBillLine[];
  waivedLines: HssBillLine[];
  hssSubsidy: number;
  clinicWriteOff: number;
  patientOop: number;
  patientPayable: number;
  patientShare: number;
  extraDue: number;
  refundDue: number;
  /** Hss:PrepaidSurplusToAccount: prepaid surplus is kept as patient account credit (Account tender) instead of a refund. */
  surplusToAccount?: boolean;
  accountCredit?: number;
  deliveryWaived: boolean;
}

/** GET /api/TeleVisit/{visitULID}/hss */
export interface HssAssessment {
  visitUlid: string;
  featureEnabled: boolean;
  offerable: boolean;
  eligible: boolean;
  applied: boolean;
  defaultApply: boolean;
  writesAtFinish: boolean;
  invalidReasons: string[];
  patientInvalidReasons: string[];
  notes: string[];
  conditions: HssCondition[];
  complicationCodes: string[];
  isChronic: boolean;
  type?: string | null;
  rateGroup?: string | null;
  patientType?: string | null;
  cardTypeUsed?: string | null;
  isSingaporean: boolean;
  patientOop: number;
  claimAmount: number;
  consultAmount: number;
  gstAmount: number;
  bill?: HssBillPreview | null;
  approvedMedicinesNote: string;
  chas?: ChasStatus | null;
  excludedPartnerCode?: string | null;
}
